<template>
  <section class="notes-card" :class="{ card: !embedded }">
    <p class="section-title">{{ $t('OPPORTUNITY_DETAILS.NOTES') }}</p>
    <div v-if="isComposerDisabled" class="note-form-locked">
      {{ lockedMessage }}
    </div>
    <div v-else class="note-form">
      <woot-message-editor
        v-model="noteContent"
        class="note-editor"
        :placeholder="$t('OPPORTUNITY_DETAILS.NOTE_PLACEHOLDER')"
        :enable-suggestions="false"
      />
      <div class="note-action-row">
        <woot-button
          size="small"
          :disabled="!noteContent.trim() || isSubmitting"
          @click="submitNote"
        >
          {{ $t('OPPORTUNITY_DETAILS.ADD_NOTE') }}
        </woot-button>
      </div>
    </div>

    <div v-if="isLoading" class="notes-loading">
      <spinner size="small" color-scheme="dark" />
    </div>
    <ul v-else class="notes-list">
      <li v-for="item in notes" :key="item.id" class="note-item">
        <p class="note-header">
          {{ formatDateTime(item.created_at) }} -
          {{ (item.user && item.user.name) || '-' }}
        </p>
        <p class="note-content">{{ item.content }}</p>
      </li>
    </ul>
  </section>
</template>

<script>
import Spinner from 'shared/components/Spinner';
import WootMessageEditor from '../../../../components/widgets/WootWriter/Editor';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';

export default {
  name: 'OpportunityNotes',
  components: {
    Spinner,
    WootMessageEditor,
  },
  mixins: [opportunityWritePermission],
  props: {
    notes: {
      type: Array,
      default: () => [],
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      noteContent: '',
    };
  },
  computed: {
    isComposerDisabled() {
      return this.isLocked || !this.canWriteOpportunities;
    },
    lockedMessage() {
      return this.isLocked
        ? this.$t('OPPORTUNITY_DETAILS.LOCKED.NOTES_DISABLED')
        : this.$t('OPPORTUNITY_DETAILS.LOCKED.READ_ONLY');
    },
  },
  methods: {
    submitNote() {
      const content = this.noteContent.trim();
      if (!content || this.isSubmitting || this.isComposerDisabled) {
        return;
      }
      this.noteContent = '';
      this.$emit('add-note', content, () => {
        this.noteContent = content;
      });
    },
    formatDateTime(value) {
      if (!value) {
        return '-';
      }
      return new Date(value).toLocaleString('pt-BR');
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-medium);
  padding: var(--space-medium);
}

.section-title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-normal);
}

.note-form-locked {
  background: var(--s-50);
  border: 1px dashed var(--s-200);
  border-radius: var(--border-radius-small);
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin-bottom: var(--space-normal);
  padding: var(--space-normal);
  text-align: center;
}

.notes-loading {
  display: flex;
  justify-content: center;
  padding: var(--space-normal) 0;
}

.notes-list {
  list-style: none;
  margin: 0;
  max-height: 40rem;
  overflow-y: auto;
  padding: 0;
}

.note-item {
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-small);
  padding: var(--space-small);
}

.note-header {
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-micro);
}

.note-content {
  color: var(--s-700);
  margin: 0;
}

.note-form {
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-normal);
  padding: var(--space-small);
}

.note-form ::v-deep .editor-root {
  background: var(--white);
  min-height: 6.75rem;
}

.note-form ::v-deep .ProseMirror-woot-style {
  background: var(--white);
  max-height: none;
  min-height: 6.75rem;
  overflow-y: hidden;
  padding: 0 var(--space-small) var(--space-small);
}

.note-form ::v-deep .ProseMirror-menubar {
  margin-top: 0;
}

.note-action-row {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-small);
}
</style>
